import { yupResolver } from "@hookform/resolvers/yup";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useGetExistingClientDeliverables } from "../../api/services/client/clientApis";
import { IdNameList } from "../../api/services/common/models";
import {
  useGetExistingTopics,
  useMdmUpdateTopicDetails,
} from "../../api/services/mdmActivity/mdmActivityApis";
import { TopicListParams } from "../../api/services/mdmActivity/models";
import {
  AccordionHeader,
  AccordionHeaderText,
  FormController,
  MuiBox,
  MuiGrid,
  MuiModal,
  MuiTextField,
} from "../../components";
import {
  LANGUAGE_CODE,
  MAX_LENGTH_75,
  PAGE_IDS,
} from "../../helpers/constants";
import {
  localeFields,
  UnitTypesKey,
} from "../../helpers/constants/siteCoreKeys";
import { DefaultValueExceptionType } from "../../helpers/types/common";
import {
  getValueOrBlank,
  isDuplicateTopic,
  isNotEmpty,
  replaceHashWithValue,
} from "../../helpers/utils";
import { useGridContainer } from "../../hooks/useGridContainer";
import { useNavigationSiteCore } from "../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../hooks/useSiteCore";
import { userPreferredLanguageSelector } from "../../redux/slices/appState/appStateSelector";
import {
  toggleLoader,
  toggleToast,
} from "../../redux/slices/appState/appStateSlice";
import {
  activityFrameworkSelector,
  selectedUnitSelector,
  topicListSelector,
} from "../../redux/slices/mdmActivity/mdmActivitySelector";
import {
  addNewTopicToList,
  updateTopicInList,
} from "../../redux/slices/mdmActivity/mdmActivitySlice";
import { TopicListItem } from "../../redux/slices/mdmActivity/models";
import { CancelFieldModal } from "../createProgramTemplate/components/cancelFieldModal/CancelFieldModal";
import { ManageLocales } from "../createProgramTemplate/modelsAndSchemas/Models";
import { AddTopicWrapper } from "./MdmAddTopic.style";
import { MdmTopicDetails } from "./components/mdmTopicDetails/MdmTopicDetails";
import { MdmTopicExceptions } from "./components/mdmTopicExceptions/MdmTopicExceptions";
import { AddTopicSchema, LocaleKey } from "./modelsAndSchemas/ValidationSchema";
import {
  AddTopicFormDefaultValues,
  AddTopicFormKey,
  AddTopicFormType,
  topicTypeKey,
} from "./modelsAndSchemas/models";
import { addTopicSiteCoreKeys } from "./siteCore/SiteCoreKeys";

export const pageId = PAGE_IDS.MDM_ADD_TOPIC;

interface MdmAddTopicPropsType {
  show: boolean;
  handleCloseModal: () => void;
  topicId: string;
  isEditTopic: boolean;
  isEditActivity: boolean;
}

export const MdmAddTopic: FC<MdmAddTopicPropsType> = ({
  show,
  handleCloseModal,
  isEditTopic = false,
  isEditActivity = false,
  topicId,
}) => {
  const [showCancel, setShowCancel] = useState<boolean>(false);

  const [selectedCountry, setSelectedCountry] = useState("");

  const [exceptions, setExceptions] = useState<DefaultValueExceptionType[]>([]);
  const [isExceptionModified, setIsExceptionModified] =
    useState<boolean>(false);

  const deliveryRef = useRef<{ resetValues: (values?: string[]) => void }>(
    null,
  );
  const participantStatusRef = useRef<{
    resetValues: (values?: string[]) => void;
  }>(null);
  const initializedEditTopicIdRef = useRef<string | null>(null);
  const dispatch = useDispatch();

  const topicList = useSelector(topicListSelector);

  const { gridSizeMedium: gridSize, gridSizeMediumClass } =
    useGridContainer(true);

  const [isExistingRecord, setIsExistingRecord] = useState<boolean>(false);
  const [updatePayload, setUpdatePayload] = useState<TopicListItem | null>(
    null,
  );

  const {
    addTopic,
    topicDetailsHeader,
    localeTopicHeader,
    addExceptionsHeader,
    discardChangesMessage,
    addTopicToActivity,
    saveAndAddNewTopicButton,
    topicAddedMessage,
    duplicateTopicErrorMessage,
    minutesList,
    editTopicHeader,
    updateTopicButton,
    topicUpdatedMessage,
  } = addTopicSiteCoreKeys;

  const {
    commonLabels,
    fetchLabel,
    fetchComponentTitle,
    commonValidationMessages,
    fetchValueByKeyOfDropdown,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { fetchLanguageDropdownValues } = useNavigationSiteCore();

  const userSelectedLanguage = useSelector(userPreferredLanguageSelector);
  const selectedUnit = useSelector(selectedUnitSelector);
  const activityFramework = useSelector(activityFrameworkSelector);

  const [selectedTopicType, setSelectedTopicType] = useState<string>(
    topicTypeKey.existingTopic,
  );

  const [topicNameHeader, setTopicNameHeader] = useState<string>("");

  // set key value pare data and sort mandatory to first
  const mergedLanguages = useMemo(() => {
    const languages = fetchLanguageDropdownValues();
    const fields = localeFields(commonLabels);

    return languages
      .map((language) => {
        const matchingPlaceholder = fields.find(
          (placeholder) => placeholder.key === language.value,
        );
        return {
          ...language,
          ...matchingPlaceholder,
          key: language.value,
          isMandatory: userSelectedLanguage === language.value,
          isEnglish:
            language.value === LANGUAGE_CODE.EN_US &&
            userSelectedLanguage !== LANGUAGE_CODE.EN_GB,
        };
      })
      .sort((a, b) => (b.isEnglish ? 1 : 0) - (a.isEnglish ? 1 : 0))
      .sort((a, b) => (b.isMandatory ? 1 : 0) - (a.isMandatory ? 1 : 0));
  }, [commonLabels, fetchLanguageDropdownValues, userSelectedLanguage]);

  const methods = useForm<AddTopicFormType>({
    mode: "all",
    defaultValues: AddTopicFormDefaultValues,
    resolver: yupResolver(
      AddTopicSchema(
        commonValidationMessages,
        selectedUnit,
        mergedLanguages as ManageLocales[],
      ),
    ),
  });

  const {
    control,
    getValues,
    setValue,
    watch,
    resetField,
    formState: { isDirty },
    handleSubmit,
    setError,
    reset,
  } = methods;

  const {
    data: existingTopicsData,
    isLoading: existingTopicsPending,
    refetchWithParams: refetchExistingTopics,
    isError: isExistingTopicsError,
  } = useGetExistingTopics();

  const {
    data: existingDeliverablesData,
    isLoading: existingDeliverablePending,
    refetchWithParams: refetchExistingDeliverable,
    isError: isExistingDeliverableError,
  } = useGetExistingClientDeliverables();

  const {
    mutate: updateTopicApi,
    isSuccess: isUpdateSuccess,
    isPending: isUpdatePending,
    isError: isUpdateError,
  } = useMdmUpdateTopicDetails();

  useEffect(() => {
    refetchExistingTopics();
    refetchExistingDeliverable({ frameworkType: activityFramework });
  }, []);

  useEffect(() => {
    if (!show || !isEditTopic || !topicId) {
      initializedEditTopicIdRef.current = null;
      return;
    }

    const topicToBeEdited = topicList?.find(
      (t: TopicListItem) => t.id === topicId,
    );

    if (!topicToBeEdited || initializedEditTopicIdRef.current === topicId) {
      return;
    }

    const localeValues = getLocaleValues(topicToBeEdited);
    const deliverablesIds = getDeliverableIds(topicToBeEdited);

    initializedEditTopicIdRef.current = topicId;
    resetForm(topicToBeEdited, localeValues, deliverablesIds);

    setTopicNameHeader(topicToBeEdited.topic);
    // only textbox for topic name in edit scenario
    setSelectedTopicType(topicTypeKey.newTopic);

    if (Array.isArray(topicToBeEdited.defaultValueExceptions)) {
      setExceptions(topicToBeEdited.defaultValueExceptions);
    }

    setIsExistingRecord(topicToBeEdited.isExisting ?? false);
  }, [show, isEditTopic, topicId, selectedUnit, topicList]);

  useEffect(() => {
    if (!isEditTopic || !topicId) return;

    const topicToBeEdited = topicList?.find(
      (t: TopicListItem) => t.id === topicId,
    );

    if (topicToBeEdited) {
      resetRefs(topicToBeEdited, getDeliverableIds(topicToBeEdited));
    }
  }, [existingDeliverablesData, isEditTopic, topicId, topicList]);

  const getLocaleValues = (topic: TopicListItem) =>
    topic.localizedTopics?.reduce(
      (acc, locale) => ({ ...acc, [locale.localeKey]: locale.name }),
      {},
    );

  const getDeliverableIds = (topic: TopicListItem) =>
    topic.deliverables.map((item: IdNameList) => item.id);

  const mandatoryLangKey = useMemo(() => {
    return mergedLanguages.find((lang) => lang.isMandatory)?.key ?? "en-US";
  }, [mergedLanguages]) as LocaleKey;

  const topicName = useWatch({ name: AddTopicFormKey.topic, control });
  const mandatoryLocaleValue = useWatch({ name: mandatoryLangKey, control });
  const resetForm = (
    topicToBeEdited: TopicListItem,
    localeValues: Record<string, string>,
    deliverablesIds: string[],
  ) => {
    reset({
      ...AddTopicFormDefaultValues,
      [AddTopicFormKey.topicType]: topicTypeKey.newTopic,
      [AddTopicFormKey.topic]: topicToBeEdited.topic,
      [AddTopicFormKey.activityStatus]: topicToBeEdited.activityStatus,
      [AddTopicFormKey.participantStatus]: topicToBeEdited.candidateStatus,
      [AddTopicFormKey.triggerBilling]: topicToBeEdited.triggerBilling,
      [AddTopicFormKey.lastContactDate]:
        topicToBeEdited.countsTowardsLastContactDate,
      [AddTopicFormKey.triggerTouch]: topicToBeEdited.triggerTouchPointSurvey,
      [AddTopicFormKey.measurementMinute]:
        selectedUnit === topicToBeEdited.unit &&
        topicToBeEdited.unit === UnitTypesKey.Minutes
          ? topicToBeEdited.measurement
          : "",
      [AddTopicFormKey.measurementOther]:
        selectedUnit === topicToBeEdited.unit &&
        topicToBeEdited.unit !== UnitTypesKey.Minutes
          ? Number(topicToBeEdited.measurement)
          : null,
      [AddTopicFormKey.deliverables]: deliverablesIds,
      ...localeValues,
    });
  };

  const resetRefs = (
    topicToBeEdited: TopicListItem,
    deliverablesIds: string[],
  ) => {
    setTimeout(() => {
      if (isNotEmpty(existingDeliverablesData) && deliveryRef.current) {
        deliveryRef.current?.resetValues(deliverablesIds);
      }
      if (
        isNotEmpty(topicToBeEdited.candidateStatus) &&
        participantStatusRef.current
      ) {
        participantStatusRef.current?.resetValues(
          topicToBeEdited.candidateStatus,
        );
      }
    }, 0); // allow the ref to mount
  };

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible:
          existingDeliverablePending ||
          existingTopicsPending ||
          isUpdatePending,
        message: commonLabels.loadingPleaseWait,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [existingDeliverablePending, existingTopicsPending, isUpdatePending]);

  useEffect(() => {
    if (isExistingDeliverableError || isExistingTopicsError || isUpdateError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isExistingDeliverableError, isExistingTopicsError, isUpdateError]);

  const closeMainModalHandler = () => {
    if (isDirty || isExceptionModified) setShowCancel(true);
    else handleCloseModal();
  };

  const onConfirmClick = () => {
    if (isEditTopic) {
      handleSubmit(() => onSubmitClick("updateTopic"))();
      return;
    }

    handleSubmit(() => onSubmitClick("add"))();
  };

  const onSecondaryButtonClick = () => {
    handleSubmit(() => onSubmitClick("saveAndAdd"))();
  };

  const buildTopicPayload = (): TopicListItem => {
    const {
      topicType,
      topic,
      deliverables,
      activityStatus,
      participantStatus,
      triggerBilling,
      lastContactDate,
      triggerTouch,
    } = getValues();

    const enrichedDeliverables = enrichDeliverables(deliverables);
    const measurementValue = getMeasurementValue();
    const { topicName } = getTopicDetails(topicType, topic);

    return {
      id: isEditTopic ? topicId : String(topicList.length + 1),
      unit: selectedUnit,
      topicType,
      topic: topicName,
      deliverables: enrichedDeliverables,
      measurement: measurementValue,
      activityStatus,
      candidateStatus: participantStatus,
      triggerBilling,
      countsTowardsLastContactDate: lastContactDate,
      triggerTouchPointSurvey: triggerTouch,
      localizedTopics: mergedLanguages
        .map((lang: any) => ({
          name: getValues(lang.key)?.trim(),
          localeKey: lang.key,
        }))
        .filter((lt) => lt.name !== ""),
      defaultValueExceptions: exceptions.map((ex) => ({
        countryCode: ex.countryCode,
        units: selectedUnit,
        measurement: ex.measurement,
        country: ex.country ?? "",
        measurementName: ex.measurementName ?? "",
      })),
      isExisting: isExistingRecord,
      editFlowChanged: (isEditActivity && !isEditTopic) || isEditTopic,
      //  to check on activity weather new topic is added during edit activity or existing topic is updated
    };
  };

  useEffect(() => {
    const topicIdOrName = getValues(AddTopicFormKey.topic);

    const updateFirstMandatoryLocale = (value: string) => {
      const firstMandatoryLang = mergedLanguages.find(
        (lang) => lang.isMandatory && lang.key,
      );

      if (
        firstMandatoryLang?.key &&
        firstMandatoryLang.key === userSelectedLanguage
      ) {
        setValue(firstMandatoryLang.key as LocaleKey, value, {
          shouldValidate: true,
        });
      }
    };

    if (!topicIdOrName) {
      updateFirstMandatoryLocale("");
      return;
    }

    if (selectedTopicType === topicTypeKey.existingTopic) {
      const selectedTopic = existingTopicsData?.find(
        (t: TopicListItem) => t.id === topicIdOrName,
      );

      if (selectedTopic?.name) {
        updateFirstMandatoryLocale(selectedTopic.name);
      }
    }

    if (selectedTopicType === topicTypeKey.newTopic) {
      updateFirstMandatoryLocale(topicIdOrName);
    }
  }, [selectedTopicType, topicName, userSelectedLanguage]);

  useEffect(() => {
    if (selectedTopicType !== topicTypeKey.newTopic) return;

    if (mandatoryLocaleValue !== topicName) {
      setValue(AddTopicFormKey.topic, mandatoryLocaleValue ?? "", {
        shouldValidate: true,
      });
    }
  }, [mandatoryLocaleValue, selectedTopicType]);

  useEffect(() => {
    if (isUpdateSuccess && updatePayload) {
      handleTopicUpdate(updatePayload);
    }
  }, [isUpdateSuccess, updatePayload]);

  const onSubmitClick = (action: "add" | "saveAndAdd" | "updateTopic") => {
    const { topicType, topic } = getValues();
    const { topicName } = getTopicDetails(topicType, topic);

    if (isTopicDuplicate(topicType, topicName)) return;

    const reduxPayload = buildTopicPayload();

    if (isEditTopic && action === "updateTopic") {
      if (isExistingRecord) {
        setUpdatePayload(reduxPayload); // Store for useEffect
        const apiPayload = mapToApiPayload(reduxPayload);
        updateTopicApi(apiPayload);
        return; // Exit; rest handled in useEffect
      }

      // not existing → local update only
      handleTopicUpdate(reduxPayload);
    } else if (action !== "updateTopic") {
      handleTopicAdd(action, reduxPayload);
    }
  };

  const handleTopicUpdate = (payload: TopicListItem) => {
    // Update existing topic in Redux
    dispatch(updateTopicInList(payload));

    dispatch(
      toggleToast({
        open: true,
        message: getAddTopicLabel(topicUpdatedMessage),
      }),
    );
    handleCloseModal();
    setUpdatePayload(null); // Clear the update payload
  };

  const handleTopicAdd = (
    action: "add" | "saveAndAdd",
    reduxPayload: TopicListItem,
  ) => {
    dispatch(addNewTopicToList(reduxPayload));

    dispatch(
      toggleToast({
        open: true,
        message: getAddTopicLabel(topicAddedMessage),
      }),
    );

    if (action === "add") {
      handleCloseModal();
    } else if (action === "saveAndAdd") {
      reset(AddTopicFormDefaultValues);
      if (deliveryRef.current) {
        deliveryRef.current.resetValues([]);
      }
      if (participantStatusRef.current) {
        participantStatusRef.current.resetValues([]);
      }
      setExceptions([]);
      setSelectedCountry("");
      setSelectedTopicType(topicTypeKey.existingTopic);
      setIsExceptionModified(false);
    }
  };

  const mapToApiPayload = (payload: TopicListItem): TopicListParams => {
    return {
      id: payload.id,
      topic: payload.topic,
      deliverableIds: payload.deliverables.map((d) => d.id),
      measurement: payload.measurement,
      activityStatus: payload.activityStatus,
      candidateStatus: payload.candidateStatus,
      triggerBilling: payload.triggerBilling,
      countsTowardsLastContactDate: payload.countsTowardsLastContactDate,
      triggerTouchPointSurvey: payload.triggerTouchPointSurvey,
      localizedTopics: payload.localizedTopics.map(({ name, localeKey }) => ({
        name,
        localeKey,
      })),
      defaultValueExceptions: payload.defaultValueExceptions.map((ex) => ({
        countryCode: ex.countryCode,
        units: ex.units,
        measurement: ex.measurement,
      })),
    };
  };

  const getTopicDetails = (topicType: string, topic: string) => {
    const topicName =
      topicType === topicTypeKey.existingTopic
        ? (existingTopicsData.find((t: IdNameList) => t.id === topic)?.name ??
          "")
        : topic;

    return { topicName };
  };

  const isTopicDuplicate = (topicType: string, topicName: string) => {
    const isDuplicate =
      topicType === topicTypeKey.newTopic &&
      isDuplicateTopic(topicList, topicName, isEditTopic ? topicId : undefined);

    if (isDuplicate) {
      setError(AddTopicFormKey.topic, {
        type: "manual",
        message: getAddTopicLabel(duplicateTopicErrorMessage),
      });
      return true;
    }
    return false;
  };

  const enrichDeliverables = (deliverables: string[]) => {
    return deliverables.map((id) => {
      const match = existingDeliverablesData.find(
        (item: IdNameList) => item.id === id,
      );
      return {
        id,
        name: match?.name ?? "",
      };
    });
  };

  const getMeasurementValue = () => {
    if (selectedUnit === UnitTypesKey.Minutes) {
      const val = getValues(AddTopicFormKey.measurementMinute);
      return val != null ? String(val) : "";
    }
    const val = getValues(AddTopicFormKey.measurementOther);
    return val != null ? String(val) : "1";
  };

  const renderHeader = (labelKey: string) => {
    return <AccordionHeaderText label={getAddTopicLabel(labelKey)} />;
  };

  const getAddTopicLabel = (key: string) => {
    return fetchLabel(addTopic, key);
  };

  const addExceptionHandler = () => {
    const { country, countryMeasurementMinute, countryMeasurementOther } =
      getValues();

    let measurement: string = "";
    if (selectedUnit === UnitTypesKey.Minutes) {
      measurement = countryMeasurementMinute;
    } else if (
      selectedUnit === UnitTypesKey.Count ||
      selectedUnit === UnitTypesKey.Days
    ) {
      measurement = countryMeasurementOther.toString();
    } else {
      measurement = "1";
    }
    setExceptions((prev) => [
      ...prev,
      {
        countryCode: selectedCountry,
        units: selectedUnit,
        country: country,
        measurement: measurement,
        measurementName:
          selectedUnit === UnitTypesKey.Minutes
            ? fetchValueByKeyOfDropdown(addTopic, minutesList, measurement)
            : measurement,
      },
    ]);
    setIsExceptionModified(true);
    resetField(AddTopicFormKey.country);
    setSelectedCountry("");
  };

  useEffect(() => {
    resetField(AddTopicFormKey.countryMeasurementOther);
    resetField(AddTopicFormKey.countryMeasurementMinute);
  }, [watch(AddTopicFormKey.country)]);

  const deleteAction = (countryCode: string) => {
    const idx = exceptions.findIndex(
      (item) => item.countryCode === countryCode,
    );
    if (idx > -1) {
      setExceptions((prev) => prev.filter((item, i) => i !== idx));
      setIsExceptionModified(true);
    }
  };

  const closeModalHandler = () => setShowCancel(false);

  const cancelConfirmHandler = () => {
    setShowCancel(false);
    handleCloseModal();
  };

  return (
    <MuiModal
      open={show}
      onClose={closeMainModalHandler}
      title={
        isEditTopic
          ? replaceHashWithValue(
              getAddTopicLabel(editTopicHeader),
              getValueOrBlank(topicNameHeader),
            )
          : fetchComponentTitle(addTopic)
      }
      primaryButtonTitle={
        isEditTopic
          ? getAddTopicLabel(updateTopicButton)
          : getAddTopicLabel(addTopicToActivity)
      }
      secondaryButtonTitle={
        !isEditTopic ? getAddTopicLabel(saveAndAddNewTopicButton) : ""
      }
      onPrimaryButtonClick={onConfirmClick}
      onSecondaryButtonClick={onSecondaryButtonClick}
      actionButtonTitle={commonLabels.back}
      onActionButtonClick={() => closeMainModalHandler()}
      hasLargeContent
      className="add-edit-mdm-topic-details"
    >
      <AddTopicWrapper>
        <FormProvider {...methods}>
          <MuiBox className="accordion-header" data-testid="topic-details">
            <AccordionHeader
              title={renderHeader(topicDetailsHeader)}
              isExpanded={true}
            >
              <MdmTopicDetails
                pageId={pageId}
                deliveryRef={deliveryRef}
                participantStatusRef={participantStatusRef}
                existingDeliverablesData={existingDeliverablesData}
                existingTopicsData={existingTopicsData}
                selectedTopicType={selectedTopicType}
                setSelectedTopicType={setSelectedTopicType}
                isEditTopic={isEditTopic}
                topicId={topicId}
              />
            </AccordionHeader>
          </MuiBox>

          <MuiBox className="accordion-header" data-testid="locale-topic">
            <AccordionHeader
              title={renderHeader(localeTopicHeader)}
              isExpanded={true}
            >
              <MuiBox className="locale-container">
                <MuiGrid container>
                  {mergedLanguages.map(
                    (
                      {
                        key,
                        placeholderKey,
                        label,
                        isMandatory,
                        isEnglish,
                      }: any, //any with type
                    ) => (
                      <MuiGrid
                        key={key}
                        size={gridSize}
                        className={gridSizeMediumClass}
                      >
                        <FormController control={control} name={key}>
                          <MuiTextField
                            label={label}
                            placeholder={placeholderKey}
                            id={`${pageId}-${key}`}
                            isMandatory={isMandatory || isEnglish}
                            maxLength={MAX_LENGTH_75}
                            showCloseIcon
                            requiredARIALabel={commonARIALabel.required}
                            tooltipMessage={
                              commonValidationMessages.fieldSpecialCharactersTooltipMessage
                            }
                            tooltipHeaderTitle={
                              commonValidationMessages.allowedSpecialCharactersTitle
                            }
                          />
                        </FormController>
                      </MuiGrid>
                    ),
                  )}
                </MuiGrid>
              </MuiBox>
            </AccordionHeader>
          </MuiBox>

          <MuiBox className="dashed-line" />

          <MuiBox className="accordion-header" data-testid="exceptions">
            <AccordionHeader
              title={renderHeader(addExceptionsHeader)}
              isExpanded={true}
            >
              <MuiBox className="exception-container">
                <MdmTopicExceptions
                  pageId={pageId}
                  setSelectedCountry={setSelectedCountry}
                  addExceptionHandler={addExceptionHandler}
                  deleteAction={deleteAction}
                  rows={exceptions}
                  isEditTopic={isEditTopic}
                />
              </MuiBox>
            </AccordionHeader>
          </MuiBox>
        </FormProvider>

        {showCancel && (
          <CancelFieldModal
            showModal={showCancel}
            handleModalClose={closeModalHandler}
            handlePrimaryButtonClick={cancelConfirmHandler}
            cancelMessage={getAddTopicLabel(discardChangesMessage)}
          />
        )}
      </AddTopicWrapper>
    </MuiModal>
  );
};

export default MdmAddTopic;
